import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  CameraAlt as CameraIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { BrowserMultiFormatReader } from '@zxing/library';

/**
 * BarcodeScanner Component
 * Uses device camera to scan barcodes and return the scanned value
 * 
 * Props:
 * - open: boolean - whether dialog is open
 * - onClose: function - callback when dialog closes
 * - onScan: function(barcode) - callback with scanned barcode value
 * - title: string - dialog title (optional)
 */
const BarcodeScanner = ({ open, onClose, onScan, title = 'Scan Barcode' }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [lastScanned, setLastScanned] = useState(null);
  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  
  // Initialize barcode reader
  useEffect(() => {
    if (!readerRef.current) {
      readerRef.current = new BrowserMultiFormatReader();
    }
    
    return () => {
      if (readerRef.current) {
        readerRef.current.reset();
      }
    };
  }, []);
  
  // Get available cameras when dialog opens
  useEffect(() => {
    if (open) {
      listCameras();
    } else {
      stopScanning();
    }
  }, [open]);
  
  // List available camera devices
  const listCameras = async () => {
    try {
      const devices = await readerRef.current.listVideoInputDevices();
      setCameraDevices(devices);
      
      if (devices.length > 0) {
        // Try to select back camera on mobile, otherwise use first camera
        const backCamera = devices.find(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('rear')
        );
        setSelectedDevice(backCamera?.deviceId || devices[0].deviceId);
        startScanning(backCamera?.deviceId || devices[0].deviceId);
      } else {
        setError('No cameras found. Please check camera permissions.');
      }
    } catch (err) {
      console.error('Error listing cameras:', err);
      setError('Failed to access camera. Please grant camera permissions.');
    }
  };
  
  // Start scanning with selected camera
  const startScanning = async (deviceId) => {
    if (!videoRef.current || !readerRef.current) return;
    
    try {
      setIsScanning(true);
      setError(null);
      
      await readerRef.current.decodeFromVideoDevice(
        deviceId,
        videoRef.current,
        (result, err) => {
          if (result) {
            const barcodeValue = result.getText();
            console.log('Barcode scanned:', barcodeValue);
            setLastScanned(barcodeValue);
            
            // Call the onScan callback
            if (onScan) {
              onScan(barcodeValue);
            }
            
            // Auto-close after successful scan (optional)
            setTimeout(() => {
              handleClose();
            }, 1000);
          }
          
          if (err && err.name !== 'NotFoundException') {
            console.error('Scanning error:', err);
          }
        }
      );
    } catch (err) {
      console.error('Error starting scanner:', err);
      setError('Failed to start camera. Please try again.');
      setIsScanning(false);
    }
  };
  
  // Stop scanning and release camera
  const stopScanning = () => {
    if (readerRef.current) {
      readerRef.current.reset();
    }
    setIsScanning(false);
  };
  
  // Handle dialog close
  const handleClose = () => {
    stopScanning();
    setError(null);
    setLastScanned(null);
    onClose();
  };
  
  // Switch camera
  const switchCamera = () => {
    const currentIndex = cameraDevices.findIndex(d => d.deviceId === selectedDevice);
    const nextIndex = (currentIndex + 1) % cameraDevices.length;
    const nextDevice = cameraDevices[nextIndex].deviceId;
    
    stopScanning();
    setSelectedDevice(nextDevice);
    startScanning(nextDevice);
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Success message */}
        {lastScanned && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Scanned: {lastScanned}
          </Alert>
        )}
        
        {/* Camera feed */}
        <Box 
          sx={{ 
            position: 'relative',
            width: '100%',
            minHeight: '300px',
            backgroundColor: '#000',
            borderRadius: 1,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <video
            ref={videoRef}
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '400px'
            }}
          />
          
          {/* Loading indicator */}
          {!isScanning && !error && (
            <Box 
              sx={{ 
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            >
              <CircularProgress sx={{ color: 'white' }} />
            </Box>
          )}
          
          {/* Scanning overlay */}
          {isScanning && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '80%',
                height: '60%',
                border: '2px solid #4CAF50',
                borderRadius: 2,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
                pointerEvents: 'none'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: '#4CAF50',
                  animation: 'scan 2s ease-in-out infinite',
                  '@keyframes scan': {
                    '0%, 100%': { transform: 'translateY(-100px)' },
                    '50%': { transform: 'translateY(100px)' }
                  }
                }}
              />
            </Box>
          )}
        </Box>
        
        {/* Instructions */}
        <Typography 
          variant="body2" 
          color="text.secondary" 
          align="center"
          sx={{ mt: 2 }}
        >
          Position the barcode within the frame. Scanning will happen automatically.
        </Typography>
        
        {/* Camera info */}
        {isScanning && cameraDevices.length > 0 && (
          <Typography 
            variant="caption" 
            color="text.secondary" 
            align="center"
            display="block"
            sx={{ mt: 1 }}
          >
            Using: {cameraDevices.find(d => d.deviceId === selectedDevice)?.label || 'Camera'}
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions>
        {/* Switch camera button (if multiple cameras) */}
        {cameraDevices.length > 1 && (
          <Button
            startIcon={<RefreshIcon />}
            onClick={switchCamera}
            disabled={!isScanning}
          >
            Switch Camera
          </Button>
        )}
        
        <Button onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarcodeScanner;